import React, { useState, useEffect, useCallback, useRef, FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import "./checkout.css";

const stripePromise = loadStripe("pk_live_51PX66aCTXHQWQf5M7rEymc9OKIMthsotT3xaMCF91VzjnSJjhAR4T7krET5YDipGutfCLXu3i6ptSaXKZVydnBcn00TIDF42nT");

interface Product {
  id: number;
  name: string;
  price: number;
  priceId: string;
  description: string;
  dimensions: string;
  images: string[];
  quantity?: number;
}

const allProducts: Product[] = [
  {
    id: 1,
    name: 'New Era 9Forty Diamond Era “Conglomerator&Co” Snapback',
    price: 100.00,
    priceId: "price_1RZDlACTXHQWQf5M6jZ8gYCZ",
    description: "- Shipping/Handling\n- Taxes",
    dimensions: "One size fits all",
    images: [require('./media/images/conglomeratorco_snapback.JPEG')],
  },
  {
    id: 2,
    name: 'Tycoonland Sticker',
    price: 7.99,
    priceId: "price_1RYpwnCTXHQWQf5MWNGS47eO",
    description: "- Shipping/Handling\n- Taxes",
    dimensions: "2.9” x 5”",
    images: [require('./media/images/tycoon_sticker.JPEG')],
  },
  {
    id: 3,
    name: 'Questgrid Icon Sticker',
    price: 5.00,
    priceId: "price_1RYpwlCTXHQWQf5MWgTD97Bq",
    description: "- Shipping/Handling\n- Taxes",
    dimensions: "3.4” x 3.5”",
    images: [require('./media/images/iconsticker.PNG')],
  },
  {
    id: 4,
    name: 'Stockbroker TycoonLand Sticker',
    price: 7.99,
    priceId: "price_1RYpwjCTXHQWQf5MqMRgt3XV",
    description: "- Shipping/Handling\n- Taxes",
    dimensions: "4.2” x 4.2”",
    images: [require('./media/images/stockbrokertycoon_sticker.PNG')],
  },
  {
    id: 5,
    name: 'Questgrid Sticker',
    price: 5.00,
    priceId: "price_1RYpwfCTXHQWQf5MBxwZm0e3",
    description: "- Shipping/Handling\n- Taxes",
    dimensions: "2” x 4.5”",
    images: [require('./media/images/questgridsticker.JPEG')],
  },
];

const CheckoutForm: React.FC = () => {
  const location = useLocation();
  const [email, setEmail] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<Product[]>([]);
  const loadedOnce = useRef<boolean>(false);

  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;

    const params = new URLSearchParams(location.search);
    const buyNowId = parseInt(params.get("buyNow") || "");
    const quantity = parseInt(params.get("quantity") || "1");

    if (buyNowId) {
      const product = allProducts.find((p) => p.id === buyNowId);
      if (product) {
        setCartItems([{ ...product, quantity }]);
      }
    } else {
      const rawCart = JSON.parse(localStorage.getItem("cart") || "[]");
      const enrichedCart = rawCart
        .map(({ id, quantity }: { id: number; quantity: number }) => {
          const product = allProducts.find((p) => p.id === id);
          return product ? { ...product, quantity } : null;
        })
        .filter(Boolean) as Product[];
      setCartItems(enrichedCart);
    }
  }, [location.search]);

  const saveCartToStorage = (cart: Product[]) => {
    const minimalCart = cart.map(({ id, quantity }) => ({ id, quantity }));
    localStorage.setItem("cart", JSON.stringify(minimalCart));
  };

  const updateQuantity = (id: number, delta: number) => {
    const updated = cartItems.map((item) =>
      item.id === id
        ? { ...item, quantity: Math.max(1, (item.quantity || 1) + delta) }
        : item
    );
    setCartItems(updated);
    saveCartToStorage(updated);
  };

  const removeItem = (id: number) => {
    const updated = cartItems.filter((item) => item.id !== id);
    setCartItems(updated);
    saveCartToStorage(updated);
  };

  const total = cartItems
    .reduce((sum, item) => sum + item.price * (item.quantity || 1), 0)
    .toFixed(2);

  const fetchClientSecret = useCallback(async () => {
    const response = await fetch("https://apiv2.tycoonland.nyc/api/stripe/webhooks/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customerEmail: email,
        line_items: cartItems.map((item) => ({
          price: item.priceId,
          quantity: item.quantity || 1,
        })),
      }),
    });

    const data = await response.json();
    setClientSecret(data.clientSecret);
    return data.clientSecret;
  }, [email, cartItems]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await fetchClientSecret();
  };

  if (clientSecret) {
    return (
      <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    );
  }

  return (
    <div className="checkout-layout">
      <div className="cart-left">
        {cartItems.length === 0 ? (
          <p>Your cart is empty. Add an item using “Add to Cart” or “Buy Now”.</p>
        ) : (
          cartItems.map((item) => (
            <div className="cart-item-box" key={item.id}>
              <img src={item.images?.[0]} alt={item.name} />
              <div className="item-details">
                <h4>{item.name}</h4>
                <p>{item.description}</p>
                <div className="quantity-row">
                  <button onClick={() => updateQuantity(item.id, -1)}>-</button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.id, 1)}>+</button>
                </div>
              </div>
              <div className="price-and-delete">
                <strong>${(item.price * (item.quantity || 1)).toFixed(2)}</strong>
                <button className="remove" onClick={() => removeItem(item.id)}>🗑</button>
              </div>
            </div>
          ))
        )}
      </div>

      <form className="checkout-right" onSubmit={handleSubmit}>
        <h3>Checkout</h3>
        <input
          type="email"
          required
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="summary">
          <div className="subtotal-row">
            <span>Subtotal</span>
            <span>${total}</span>
          </div>
          <small>Shipping, taxes, and discounts calculated at checkout.</small>
        </div>
        <button type="submit" disabled={cartItems.length === 0}>Pay Now</button>
      </form>
    </div>
  );
};

export default CheckoutForm;
